import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useSnackbar, useTheme, Button, Chip, Icon, IconButton, Stack, TextField, Typography } from '@emburse/embark-core';
import { CheckIcon, EditIcon, HelpIcon, UndoIcon } from '@emburse/embark-icons';
import { useCallback, useContext, useState } from 'react';
import { $t, useTranslation } from '../../../hooks/useTranslation';
import { getIconForViolationStatus } from '../util';
import { AuditAlertsContext } from '../AuditAlertsProvider';
import IconPopover from '../../../components/IconPopover';
import { setAuditFlagStatus, setAuditFlagNotes } from '../../../api/audit';
export default function AlertsDrawerFlag({ auditFlag }) {
    useTranslation();
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const { auditDefinitions, updateAuditFlagStatus } = useContext(AuditAlertsContext);
    const [isEditingNote, setIsEditingNote] = useState(false);
    const [notes, setNotes] = useState(auditFlag.Notes);
    const getBackgroundColor = useCallback(() => {
        switch (auditFlag.Status) {
            case 'V':
                return '#FEECEB';
            case 'NV':
                return '#EDF7ED';
            default:
                return theme.palette.background.default;
        }
    }, [auditFlag.Status, theme.palette.background.default]);
    return (_jsxs(Stack, { direction: "row", spacing: 1, sx: {
            width: '100%',
            backgroundColor: getBackgroundColor(),
            paddingBottom: 2,
            marginRight: 2,
            paddingTop: 1
        }, "data-qa": "alerts-drawer-flag", children: [_jsx(Stack, { alignItems: "flex-start", sx: {
                    height: '100%',
                    paddingTop: 2,
                    paddingLeft: 1
                }, "data-qa": "alerts-drawer-flag-icon", children: _jsx(Icon, { iconUrl: getIconForViolationStatus(auditFlag.Status) }) }), _jsxs(Stack, { alignItems: "flex-start", spacing: 2, sx: { width: '100%', paddingRight: 2 }, children: [_jsxs(Stack, { alignItems: "flex-start", justifyContent: "flex-start", children: [auditFlag.Status !== 'F' && (_jsxs(Stack, { alignItems: "flex-start", justifyContent: "center", sx: { paddingTop: 1.2 }, "data-qa": "alerts-drawer-flag-status", children: [auditFlag.Status === 'NV' && (_jsx(Chip, { label: $t('Marked No Violation'), color: "success", size: "mini", variant: "outlined" })), auditFlag.Status === 'V' && (_jsx(Chip, { label: $t('Marked Violation'), color: "error", size: "mini", variant: "outlined" }))] })), _jsxs(Stack, { direction: "row", sx: { paddingTop: 0.1 }, children: [_jsx(Typography, { color: "textSecondary", variant: "body1", sx: { fontWeight: 500 }, "data-qa": "alerts-drawer-flag-name", children: $t(auditDefinitions.find((definition) => definition.AuditDefPK === auditFlag.AuditDefPK)?.Name) }), _jsx(IconPopover, { title: "Rule Details", content: $t(auditDefinitions.find((definition) => definition.AuditDefPK === auditFlag.AuditDefPK)?.Description), iconUrl: HelpIcon, "data-qa": "alerts-drawer-flag-help" })] }), _jsx(Typography, { color: "textSecondary", variant: "body2", "data-qa": "alerts-drawer-flag-description", children: $t(auditFlag.ShortDescription) })] }), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(Button, { color: "monochrome", disabled: auditFlag.Status === 'V', onClick: async () => {
                                    try {
                                        await setAuditFlagStatus({
                                            AuditFlagPK: auditFlag.AuditFlagPK,
                                            Status: 'V'
                                        });
                                        enqueueSnackbar($t('Successfully marked as violation'), {
                                            variant: 'success'
                                        });
                                        updateAuditFlagStatus(auditFlag.AuditFlagPK, 'V');
                                    }
                                    catch (e) {
                                        enqueueSnackbar(e.message, { variant: 'error' });
                                    }
                                }, "data-qa": "alerts-drawer-flag-violation-button", children: $t('Violation') }), _jsx(Button, { color: "monochrome", disabled: auditFlag.Status === 'NV', onClick: async () => {
                                    try {
                                        await setAuditFlagStatus({
                                            AuditFlagPK: auditFlag.AuditFlagPK,
                                            Status: 'NV'
                                        });
                                        enqueueSnackbar($t('Successfully marked as no violation'), {
                                            variant: 'success'
                                        });
                                        updateAuditFlagStatus(auditFlag.AuditFlagPK, 'NV');
                                    }
                                    catch (e) {
                                        enqueueSnackbar(e.message, { variant: 'error' });
                                    }
                                }, "data-qa": "alerts-drawer-flag-no-violation-button", children: $t('No Violation') }), _jsx(IconButton, { disabled: auditFlag.Status === 'F', onClick: async () => {
                                    try {
                                        await setAuditFlagStatus({
                                            AuditFlagPK: auditFlag.AuditFlagPK,
                                            Status: 'F'
                                        });
                                        enqueueSnackbar($t('Successfully removed violation status'), {
                                            variant: 'success'
                                        });
                                        updateAuditFlagStatus(auditFlag.AuditFlagPK, 'F');
                                    }
                                    catch (e) {
                                        enqueueSnackbar(e.message, { variant: 'error' });
                                    }
                                }, "data-qa": "alerts-drawer-flag-clear-button", children: _jsx(Icon, { iconUrl: UndoIcon }) })] }), _jsxs(Stack, { direction: "row", sx: { width: '100%' }, children: [_jsx(TextField, { value: notes, onChange: (e) => setNotes(e.target.value), readOnly: !isEditingNote, label: $t('Note'), fullWidth: true, "data-qa": "alerts-drawer-flag-notes" }), _jsx(IconButton, { onClick: async () => {
                                    try {
                                        if (isEditingNote) {
                                            await setAuditFlagNotes(auditFlag.AuditFlagPK, notes);
                                            enqueueSnackbar($t('Successfully updated note'), {
                                                variant: 'success'
                                            });
                                        }
                                        setIsEditingNote(!isEditingNote);
                                    }
                                    catch (e) {
                                        enqueueSnackbar(e.message, { variant: 'error' });
                                    }
                                }, "data-qa": "alerts-drawer-flag-edit-button", children: _jsx(Icon, { iconUrl: isEditingNote ? CheckIcon : EditIcon }) })] })] })] }));
}
